import { Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { AppService, User, UserApiService } from '@mavles/user';
import { PageType } from '@mavles/shared';

import { RouteDataService } from '@mavles/shared';
import { AlertService } from '@mavles/shared';
import { MediaService } from '@mavles/shared';

@Component({
  selector: 'app-user-view',
  templateUrl: './user-view.component.html',
  styleUrl: './user-view.component.scss',
  standalone: false,
})
export class UserViewComponent implements OnInit {
  protected location = inject(Location);
  protected routeService = inject(RouteDataService);
  private alertService = inject(AlertService);
  protected appService: AppService = inject(AppService);
  protected mediaService = inject(MediaService);
  private fb = inject(FormBuilder);
  private userApi = inject(UserApiService);

  user!: User;
  userForm!: FormGroup;
  resetPasswordForm!: FormGroup;

  // UI state
  activeTab: 'profile' | 'password' = 'profile';
  isEditing = false;
  saving = false;
  uploadingPhoto = false;
  showCurrent = false;
  showNew = false;

  // Deprecated but kept for compat
  pageType = PageType.VIEW;
  pageTypes = PageType;

  passwordStrength = { pct: 0, color: '#e5e7eb', label: '' };

  get initials(): string {
    const f = this.user?.firstname?.[0] ?? '';
    const l = this.user?.lastname?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  ngOnInit(): void {
    this.loadUser();
  }

  // ================= LOAD =================

  private loadUser() {
    const user = this.appService.currentUser;
    if (user) {
      this.user = user;
      this.createUserForm(user);
    } else {
      this.appService.loadCurrentUser();
      // retry after load
      setTimeout(() => {
        if (this.appService.currentUser) {
          this.user = this.appService.currentUser;
          this.createUserForm(this.user);
        }
      }, 600);
    }
  }

  // ================= TOGGLE EDIT =================

  toggleEdit() {
    this.isEditing = !this.isEditing;
    if (this.isEditing && this.user) {
      this.createUserForm(this.user);
    }
  }

  initPasswordForm() {
    this.createPasswordForm();
  }

  // ================= SAVE PROFILE =================

  onSaveUser() {
    if (!this.userForm.valid) return;
    this.saving = true;

    this.appService.updateUser(this.userForm.value).subscribe({
      next: (user) => {
        this.user = user;
        this.isEditing = false;
        this.saving = false;
        this.alertService.success('Profile updated successfully');
      },
      error: () => {
        this.saving = false;
        this.alertService.error('Update failed, please try again');
      },
    });
  }

  // ================= AVATAR / PROFILE PHOTO =================

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Allowed types: jpeg, png, jpg, gif, webp
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      this.alertService.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      input.value = '';
      return;
    }

    // Max 5MB
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      this.alertService.error('Image size must be less than 5MB');
      input.value = '';
      return;
    }

    this.uploadingPhoto = true;
    this.userApi.uploadAvatar(file).subscribe({
      next: (res: any) => {
        this.uploadingPhoto = false;
        const updatedUser = res.data?.user || res.data;
        if (res.data?.image_id) {
          updatedUser.image_id = res.data.image_id;
        }
        this.user = { ...this.user, ...updatedUser };
        this.appService.refreshUserData(this.user);
        this.alertService.success(res.message || 'Profile photo updated successfully');
        input.value = '';
      },
      error: (err: any) => {
        this.uploadingPhoto = false;
        const msg = err?.error?.message || err?.message || 'Failed to upload profile photo';
        this.alertService.error(msg);
        input.value = '';
      }
    });
  }

  onRemoveAvatar(): void {
    this.uploadingPhoto = true;
    this.userApi.deleteAvatar().subscribe({
      next: (res: any) => {
        this.uploadingPhoto = false;
        const updatedUser = res.data?.user || res.data;
        this.user = { ...this.user, ...updatedUser, image_id: null };
        this.appService.refreshUserData(this.user);
        this.alertService.success(res.message || 'Profile photo removed successfully');
      },
      error: (err: any) => {
        this.uploadingPhoto = false;
        const msg = err?.error?.message || err?.message || 'Failed to remove profile photo';
        this.alertService.error(msg);
      }
    });
  }

  // ================= SAVE PASSWORD =================

  onSavePassword() {
    if (!this.resetPasswordForm.valid) return;
    this.saving = true;

    this.appService.updatePassword(this.resetPasswordForm.value).subscribe({
      next: () => {
        this.saving = false;
        this.resetPasswordForm.reset();
        this.passwordStrength = { pct: 0, color: '#e5e7eb', label: '' };
        this.alertService.success('Password updated successfully');
        this.activeTab = 'profile';
      },
      error: () => {
        this.saving = false;
        this.alertService.error('Password update failed');
      },
    });
  }

  // ================= PASSWORD STRENGTH =================

  onPasswordInput() {
    const val: string = this.resetPasswordForm.get('newPassword')?.value ?? '';
    const len = val.length;

    if (len === 0) {
      this.passwordStrength = { pct: 0, color: '#e5e7eb', label: '' };
    } else if (len < 6) {
      this.passwordStrength = { pct: 25, color: '#ef4444', label: 'Too short' };
    } else if (len < 8) {
      this.passwordStrength = { pct: 50, color: '#f59e0b', label: 'Weak' };
    } else if (!/[A-Z]/.test(val) || !/[0-9]/.test(val)) {
      this.passwordStrength = { pct: 75, color: '#3b82f6', label: 'Medium' };
    } else {
      this.passwordStrength = { pct: 100, color: '#10b981', label: 'Strong' };
    }
  }

  // ================= HELPERS =================

  isInvalid(field: string, form: FormGroup): boolean {
    const ctrl = form.get(field);
    return !!(ctrl?.invalid && (ctrl.touched || ctrl.dirty));
  }

  // ================= FORMS =================

  private createUserForm(user: User) {
    this.userForm = this.fb.group({
      id: [user.id],
      firstname: [user.firstname],
      lastname: [user.lastname, Validators.required],
      image: [user.image],
      phone: [user.phone, Validators.required],
    });
  }

  private createPasswordForm() {
    if (!this.resetPasswordForm) {
      this.resetPasswordForm = this.fb.group({
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
      });
    }
  }

  // Legacy compat
  editUser(type: PageType) {
    this.pageType = type;
    this.isEditing = type === PageType.UPDATE;
  }
}


